import { useState } from 'react';
import { Alert, Button, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { usePopulateStockCount } from '../api';

/** "Populate from stock on hand" (BUILD_SPEC.md §8.6, G-B): bulk-inserts one count
 * line per current stock-on-hand row at the count's site, so staff doing a full
 * physical stocktake don't have to hand-pick every product×location first. Purely
 * additive and idempotent (server `onConflictDoNothing`s on lines that already
 * exist) — no confirmation needed, unlike the destructive actions `ConfirmButton`
 * guards, since this can never overwrite a countedQty a user already entered. */
export function PopulateFromStockButton({ countId }: { countId: string }) {
  const canOperate = useCanOperate();
  const [message, setMessage] = useState<string | null>(null);
  const populate = usePopulateStockCount(countId);

  function handleClick() {
    setMessage(null);
    populate.mutate(undefined, {
      onSuccess: (result) => {
        setMessage(
          result.inserted > 0
            ? `Added ${result.inserted} line${result.inserted === 1 ? '' : 's'} from stock on hand` +
                (result.skippedExisting > 0 ? ` (${result.skippedExisting} already on the count).` : '.')
            : 'No new lines — every product/location with stock on hand at this site is already on the count.',
        );
      },
    });
  }

  return (
    <div>
      <Tooltip label="Requires Operations role" disabled={canOperate}>
        <span>
          <Button variant="default" disabled={!canOperate} loading={populate.isPending} onClick={handleClick}>
            Populate from stock on hand
          </Button>
        </span>
      </Tooltip>
      {populate.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} mt="xs">
          {populate.error instanceof ApiRequestError ? populate.error.message : populate.error?.message}
        </Alert>
      )}
      {message && (
        <Text size="sm" c="dimmed" mt="xs">
          {message}
        </Text>
      )}
    </div>
  );
}
